import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Sparkle, ArrowSquareOut } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InstructorDashboard() {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [masterclasses, setMasterclasses] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingMasterclass, setEditingMasterclass] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    date: '',
    duration: '',
    price: '',
    format: 'online',
    external_link: '',
    image_url: ''
  });

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [categoriesRes, classesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/masterclasses/instructor/${user.id}`),
        axios.get(`${API}/analytics/instructor/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCategories(categoriesRes.data.categories);
      setMasterclasses(classesRes.data);
      setAnalytics(analyticsRes.data);
    } catch {
      // silently handle fetch errors
    }
  };

  const handleGenerateDescription = async () => {
    if (!formData.title || !formData.category) {
      toast.error('Please provide title and category first');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/ai/generate-description`, {
        title: formData.title,
        category: formData.category,
        instructor_name: user.name
      });
      setFormData({ ...formData, description: response.data.description });
      toast.success('Description generated!');
    } catch (error) {
      toast.error('Failed to generate description');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API}/upload/image`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ ...formData, image_url: response.data.image_url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingMasterclass) {
        // Update existing masterclass
        await axios.patch(
          `${API}/masterclasses/${editingMasterclass.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Masterclass updated successfully!');
      } else {
        // Create new masterclass
        await axios.post(
          `${API}/masterclasses`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Masterclass created! Pending admin approval.');
      }
      
      setShowCreateDialog(false);
      setEditingMasterclass(null);
      setFormData({
        title: '',
        description: '',
        category: '',
        date: '',
        duration: '',
        price: '',
        format: 'online',
        external_link: '',
        image_url: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingMasterclass ? 'update' : 'create'} masterclass`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (masterclass) => {
    setEditingMasterclass(masterclass);
    setFormData({
      title: masterclass.title,
      description: masterclass.description,
      category: masterclass.category,
      date: masterclass.date,
      duration: masterclass.duration,
      price: masterclass.price.replace('₹', '').trim(), // Remove ₹ for editing
      format: masterclass.format,
      external_link: masterclass.external_link,
      image_url: masterclass.image_url || ''
    });
    setShowCreateDialog(true);
  };

  const handleCloseDialog = () => {
    setShowCreateDialog(false);
    setEditingMasterclass(null);
    setFormData({
      title: '',
      description: '',
      category: '',
      date: '',
      duration: '',
      price: '',
      format: 'online',
      external_link: '',
      image_url: ''
    });
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 gradient-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div onClick={() => navigate('/')} className="text-2xl font-bold tracking-tight cursor-pointer text-white flex items-center gap-0" data-testid="logo">
            <img src="/logo.png" alt="NeoMaestro" className="h-9 w-9 object-contain bg-white rounded-lg" />
            <span>NeoMaestro</span>
          </div>
          <div className="flex gap-4 items-center">
            <span className="text-white/80">Welcome, {user?.name}</span>
            <Button variant="ghost" onClick={logout} className="text-white/90 hover:text-white hover:bg-transparent" data-testid="logout-btn">
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-24 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-12">
            <div>
              <h1 className="text-5xl font-black tracking-tighter mb-4" data-testid="dashboard-title">Instructor Dashboard</h1>
              <p className="text-xl text-gray-600">Manage your masterclasses and track performance</p>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) handleCloseDialog(); else setShowCreateDialog(true); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#34c759] text-white hover:bg-[#28a745] rounded-full px-6" data-testid="create-masterclass-btn">
                  <Plus size={20} weight="bold" className="mr-2" /> Create Masterclass
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-50 border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">
                    {editingMasterclass ? 'Edit Masterclass' : 'Create New Masterclass'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="mt-2 bg-white border-gray-200 text-gray-900"
                      data-testid="title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                      <SelectTrigger className="mt-2 bg-white border-gray-200 text-gray-900" data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 text-gray-900">
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label htmlFor="description">Description</Label>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleGenerateDescription}
                        disabled={loading}
                        className="bg-[#00F0FF] text-black hover:bg-[#34c759] text-xs"
                        data-testid="generate-description-btn"
                      >
                        <Sparkle size={14} className="mr-1" /> AI Generate
                      </Button>
                    </div>
                    <Textarea
                      id="description"
                      required
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-white border-gray-200 text-gray-900"
                      rows={4}
                      data-testid="description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Date</Label>
                      <Input
                        id="date"
                        required
                        placeholder="e.g., Jan 15, 2026"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="mt-2 bg-white border-gray-200 text-gray-900"
                        data-testid="date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="duration">Duration</Label>
                      <Input
                        id="duration"
                        required
                        placeholder="e.g., 2 hours"
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                        className="mt-2 bg-white border-gray-200 text-gray-900"
                        data-testid="duration-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="price">Price</Label>
                      <Input
                        id="price"
                        required
                        placeholder="e.g., 9999 or Free"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="mt-2 bg-white border-gray-200 text-gray-900"
                        data-testid="price-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Enter amount only (₹ will be added automatically)</p>
                    </div>
                    <div>
                      <Label htmlFor="format">Format</Label>
                      <Select value={formData.format} onValueChange={(value) => setFormData({ ...formData, format: value })}>
                        <SelectTrigger className="mt-2 bg-white border-gray-200 text-gray-900" data-testid="format-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 text-gray-900">
                          <SelectItem value="online">Online</SelectItem>
                          <SelectItem value="offline">Offline</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="external_link">Registration Link (External)</Label>
                    <Input
                      id="external_link"
                      type="url"
                      required
                      placeholder="https://your-registration-page.com"
                      value={formData.external_link}
                      onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                      className="mt-2 bg-white border-gray-200 text-gray-900"
                      data-testid="external-link-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="image">Masterclass Image (Optional)</Label>
                    <div className="mt-2 space-y-3">
                      {/* File Upload */}
                      <div>
                        <input
                          type="file"
                          id="image-upload"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          onClick={() => document.getElementById('image-upload').click()}
                          disabled={uploadingImage}
                          className="w-full bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                          data-testid="upload-image-btn"
                        >
                          {uploadingImage ? 'Uploading...' : 'Upload Image'}
                        </Button>
                      </div>
                      
                      {/* Divider */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 border-t border-gray-300"></div>
                        <span className="text-xs text-gray-500">OR</span>
                        <div className="flex-1 border-t border-gray-300"></div>
                      </div>
                      
                      {/* URL Input */}
                      <Input
                        id="image_url"
                        type="text"
                        placeholder="Paste image URL..."
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        className="bg-white border-gray-200 text-gray-900"
                        data-testid="image-url-input"
                      />
                      
                      {/* Preview */}
                      {formData.image_url && (
                        <div className="mt-2">
                          <img 
                            src={formData.image_url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}` : formData.image_url} 
                            alt="Preview" 
                            className="w-full h-32 object-cover rounded border border-gray-200"
                            onError={(e) => {e.target.style.display = 'none'}}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#34c759] text-white hover:bg-[#28a745] rounded-full py-6 font-bold"
                    data-testid="submit-masterclass-btn"
                  >
                    {loading
                      ? (editingMasterclass ? 'Updating...' : 'Creating...')
                      : (editingMasterclass ? 'Update Masterclass' : 'Create Masterclass')
                    }
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Analytics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="text-4xl font-black gradient-text mb-2" data-testid="total-classes">{masterclasses.length}</div>
              <div className="text-gray-600">Total Masterclasses</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="text-4xl font-black text-[#00a3af] mb-2" data-testid="approved-classes">{masterclasses.filter(mc => mc.approved).length}</div>
              <div className="text-gray-600">Approved</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="text-4xl font-black text-[#00a3af] mb-2" data-testid="total-clicks">{analytics.reduce((sum, a) => sum + a.clicks, 0)}</div>
              <div className="text-gray-600">Total Clicks</div>
            </div>
          </div>

          {/* Masterclasses List */}
          <div>
            <h2 className="text-3xl font-bold mb-6" data-testid="my-classes-title">My Masterclasses</h2>
            {masterclasses.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
                <p className="text-gray-600 mb-4" data-testid="no-classes-message">You haven't created any masterclasses yet</p>
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-[#34c759] text-black hover:bg-[#28a745] rounded-full"
                >
                  Create Your First Masterclass
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {masterclasses.map((mc, index) => {
                  const mcAnalytics = analytics.find(a => a.masterclass_id === mc.id);
                  return (
                    <motion.div
                      key={mc.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-6 hover:border-[#CCFF00] transition-colors"
                      data-testid={`masterclass-item-${index}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold">{mc.title}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              mc.approved ? 'bg-[#34c759]/20 text-[#00a3af]' : 'bg-[#A1A1AA]/20 text-gray-600'
                            }`}>
                              {mc.approved ? 'Approved' : 'Pending'}
                            </span>
                            {mc.featured && (
                              <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#00F0FF]/20 text-[#00a3af]">
                                Featured
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-4">{mc.description.substring(0, 150)}...</p>
                          <div className="flex gap-6 text-sm">
                            <span><span className="text-gray-600">Category:</span> {mc.category}</span>
                            <span><span className="text-gray-600">Date:</span> {mc.date}</span>
                            <span><span className="text-gray-600">Price:</span> {mc.price}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-black text-[#00a3af] mb-1">{mcAnalytics?.clicks || 0}</div>
                          <div className="text-xs text-gray-600">Clicks</div>
                          <div className="flex gap-2 mt-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(mc)}
                              className="text-gray-700 hover:text-[#34c759] hover:bg-transparent border-gray-300"
                              data-testid={`edit-masterclass-${index}`}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => window.open(`/masterclass/${mc.id}`, '_blank')}
                              className="text-[#00a3af] hover:text-[#004d7a] hover:bg-transparent"
                              data-testid={`view-masterclass-${index}`}
                            >
                              View <ArrowSquareOut size={16} className="ml-0.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Plans & Pricing */}
          <div className="mt-16" data-testid="pricing-section">
            <h2 className="text-3xl font-bold mb-2">Plans & Pricing</h2>
            <p className="text-gray-600 mb-8">Your first 2 masterclass listings are free. Upgrade to list more and unlock premium features.</p>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-gray-500">Current Plan</span>
                  <h3 className="text-xl font-bold">Free</h3>
                  <p className="text-sm text-gray-600">{masterclasses.length}/2 free listings used</p>
                </div>
                <div className={`text-sm font-bold px-4 py-2 rounded-full ${masterclasses.length >= 2 ? 'bg-red-100 text-red-700' : 'bg-[#34c759]/10 text-[#34c759]'}`}>
                  {masterclasses.length >= 2 ? 'Limit Reached' : `${2 - masterclasses.length} free listing${2 - masterclasses.length !== 1 ? 's' : ''} remaining`}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 border-2 border-[#00a3af] rounded-xl p-6">
                <h3 className="text-lg font-bold mb-1">Free</h3>
                <div className="text-3xl font-black mb-3">₹0</div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><span className="text-[#34c759] font-bold">&#10003;</span> First 2 masterclass listings free</li>
                  <li className="flex items-center gap-2"><span className="text-[#34c759] font-bold">&#10003;</span> Basic analytics</li>
                  <li className="flex items-center gap-2"><span className="text-[#34c759] font-bold">&#10003;</span> Standard listing placement</li>
                </ul>
              </div>
              <div className="bg-gray-50 border-2 border-[#34c759] rounded-xl p-6 relative">
                <span className="absolute -top-3 left-4 bg-[#34c759] text-white text-xs font-bold px-3 py-1 rounded-full">Popular</span>
                <h3 className="text-lg font-bold mb-1">Professional</h3>
                <div className="text-3xl font-black mb-3">₹2,999<span className="text-sm font-normal text-gray-500">/mo</span></div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><span className="text-[#34c759] font-bold">&#10003;</span> Unlimited masterclass listings</li>
                  <li className="flex items-center gap-2"><span className="text-[#34c759] font-bold">&#10003;</span> Featured instructor badge</li>
                  <li className="flex items-center gap-2"><span className="text-[#34c759] font-bold">&#10003;</span> Advanced analytics</li>
                  <li className="flex items-center gap-2"><span className="text-[#34c759] font-bold">&#10003;</span> Priority support</li>
                </ul>
                <Button className="w-full mt-4 bg-[#34c759] text-white hover:bg-[#28a745] rounded-full" data-testid="upgrade-pro-btn">
                  Upgrade to Pro
                </Button>
              </div>
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold mb-1">Premium</h3>
                <div className="text-3xl font-black mb-3">₹5,999<span className="text-sm font-normal text-gray-500">/mo</span></div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><span className="text-[#34c759] font-bold">&#10003;</span> Everything in Professional</li>
                  <li className="flex items-center gap-2"><span className="text-[#34c759] font-bold">&#10003;</span> Homepage featured placement</li>
                  <li className="flex items-center gap-2"><span className="text-[#34c759] font-bold">&#10003;</span> Dedicated account manager</li>
                  <li className="flex items-center gap-2"><span className="text-[#34c759] font-bold">&#10003;</span> Custom branding options</li>
                </ul>
                <Button variant="outline" className="w-full mt-4 rounded-full border-gray-300" data-testid="upgrade-premium-btn">
                  Upgrade to Premium
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}